/**
 * Empresas cliente.
 *
 * Todo pasa por funciones de la base de datos que comprueban que
 * quien llama es admin: la validacion no puede vivir solo en la
 * pantalla.
 */

import { getSupabase, isSupabaseEnabled } from './supabase'

export interface OrganizationOverview {
  organizationId: string
  name: string
  contactEmail: string | null
  courseSlug: string | null
  seatsTotal: number
  /** Empleados que ya han entrado. */
  seatsClaimed: number
  /** Reservados por correo, pendientes de que entren. */
  seatsReserved: number
  expiresAt: string | null
  /** Nulo: sigue el ajuste de la plataforma. */
  videoCompletionRequired: boolean | null
  /** Dias que quedan de contrato. Null si no caduca. */
  daysLeft: number | null
}

export interface OrganizationSeat {
  seatId: string
  email: string
  userId: string | null
  assignedAt: string | null
  hasEntered: boolean
}

export async function listOrganizations(): Promise<OrganizationOverview[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('organizations_overview')
  if (error) {
    console.info('[organizations] list:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    organizationId: r.organization_id as string,
    name: r.name as string,
    contactEmail: (r.contact_email as string) ?? null,
    courseSlug: (r.course_slug as string) ?? null,
    seatsTotal: (r.seats_total as number) ?? 0,
    seatsClaimed: Number(r.seats_claimed ?? 0),
    seatsReserved: Number(r.seats_reserved ?? 0),
    expiresAt: (r.expires_at as string) ?? null,
    videoCompletionRequired:
      r.video_completion_required === null || r.video_completion_required === undefined
        ? null
        : Boolean(r.video_completion_required),
    daysLeft: r.days_left === null || r.days_left === undefined
      ? null
      : Number(r.days_left),
  }))
}

export async function createOrganization(input: {
  name: string
  contactEmail: string
  seats: number
  months?: number
}): Promise<string> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { data, error } = await supabase.rpc('create_organization_with_seats', {
    p_name: input.name,
    p_contact_email: input.contactEmail,
    p_seats: input.seats,
    p_months: input.months ?? 12,
  })

  if (error) throw new Error(error.message)
  return data as string
}

export async function listSeats(organizationId: string): Promise<OrganizationSeat[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('organization_seats', {
    p_organization_id: organizationId,
  })
  if (error) {
    console.info('[organizations] seats:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    seatId: r.seat_id as string,
    email: (r.email as string) ?? '',
    userId: (r.user_id as string) ?? null,
    assignedAt: (r.assigned_at as string) ?? null,
    hasEntered: Boolean(r.has_entered),
  }))
}

/** Devuelve cuantos asientos se han reservado de verdad. */
export async function assignSeats(
  organizationId: string,
  emails: string[],
): Promise<number> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { data, error } = await supabase.rpc('assign_seats', {
    p_organization_id: organizationId,
    p_emails: emails,
  })

  if (error) throw new Error(error.message)
  return Number(data ?? 0)
}

export async function revokeSeat(seatId: string, reason?: string): Promise<void> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { error } = await supabase.rpc('revoke_seat', {
    p_seat_id: seatId,
    p_reason: reason ?? null,
  })
  // Antes se tragaba el error y se devolvia boolean: el llamador
  // (retirar.useMutation) lo interpretaba como exito y ensenaba al
  // admin "Asiento retirado" aunque el borrado hubiera fallado.
  // Ahora es coherente con el resto del modulo (assignSeats,
  // addManager, inviteSeats): tira. D-432.
  if (error) throw new Error(error.message)
}

// ── Panel del responsable de formacion ────────────────────

export interface ManagedOrganization {
  organizationId: string
  name: string
  seatsTotal: number
  courseSlug: string | null
  expiresAt: string | null
}

export interface TeamSummary {
  seatsTotal: number
  seatsAssigned: number
  peopleStarted: number
  peopleFinished: number
  avgCompletionPct: number
  activeLast7d: number
}

export interface TeamMember {
  email: string
  hasEntered: boolean
  /** Ademas de participar, gestiona el programa. */
  isManager: boolean
  blocksDone: number
  blocksTotal: number
  completionPct: number
  lastActivity: string | null
}

/** La empresa que gestiona quien ha iniciado sesion, si gestiona alguna. */
export async function fetchMyOrganization(): Promise<ManagedOrganization | null> {
  if (!isSupabaseEnabled()) return null
  const supabase = getSupabase()
  if (!supabase) return null

  const { data, error } = await supabase.rpc('my_managed_organization')
  if (error || !data || data.length === 0) return null

  const r = data[0] as Record<string, unknown>
  return {
    organizationId: r.organization_id as string,
    name: r.name as string,
    seatsTotal: (r.seats_total as number) ?? 0,
    courseSlug: (r.course_slug as string) ?? null,
    expiresAt: (r.expires_at as string) ?? null,
  }
}

export async function fetchTeamSummary(organizationId: string): Promise<TeamSummary | null> {
  if (!isSupabaseEnabled()) return null
  const supabase = getSupabase()
  if (!supabase) return null

  const { data, error } = await supabase.rpc('team_summary', {
    p_organization_id: organizationId,
  })
  if (error || !data || data.length === 0) return null

  const r = data[0] as Record<string, unknown>
  return {
    seatsTotal: Number(r.seats_total ?? 0),
    seatsAssigned: Number(r.seats_assigned ?? 0),
    peopleStarted: Number(r.people_started ?? 0),
    peopleFinished: Number(r.people_finished ?? 0),
    avgCompletionPct: Number(r.avg_completion_pct ?? 0),
    activeLast7d: Number(r.active_last_7d ?? 0),
  }
}

export async function fetchTeamMembers(organizationId: string): Promise<TeamMember[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('team_members', {
    p_organization_id: organizationId,
  })
  if (error) {
    console.info('[organizations] team:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    email: (r.email as string) ?? '',
    hasEntered: Boolean(r.has_entered),
    isManager: Boolean(r.is_manager),
    blocksDone: Number(r.blocks_done ?? 0),
    blocksTotal: Number(r.blocks_total ?? 0),
    completionPct: Number(r.completion_pct ?? 0),
    lastActivity: (r.last_activity as string) ?? null,
  }))
}

/** Responsables de formacion de una empresa. Solo para el admin. */
export async function listManagers(organizationId: string): Promise<
  { email: string; hasEntered: boolean }[]
> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('organization_managers', {
    p_organization_id: organizationId,
  })
  if (error) return []

  return (data ?? []).map((r: Record<string, unknown>) => ({
    email: r.email as string,
    hasEntered: Boolean(r.has_entered),
  }))
}

export async function addManager(
  organizationId: string,
  email: string,
  withLicence = false,
): Promise<void> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { error } = await supabase.rpc('set_organization_manager', {
    p_organization_id: organizationId,
    p_email: email,
    p_with_licence: withLicence,
  })
  if (error) throw new Error(error.message)
}

export async function removeManager(organizationId: string, email: string): Promise<void> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { error } = await supabase.rpc('remove_organization_manager', {
    p_organization_id: organizationId,
    p_email: email,
  })
  // Antes se tragaba el error y la mutacion daba OK: el admin creia
  // que el responsable estaba fuera hasta que la refetch lo hacia
  // reaparecer sin explicacion. Se tira igual que el resto del
  // modulo. D-432.
  if (error) throw new Error(error.message)
}

/**
 * Avisa por correo a las personas indicadas de que tienen acceso.
 *
 * Devuelve cuantos salieron y cuantos fallaron: un correo mal
 * escrito no debe impedir que salgan los demas.
 */
export async function inviteSeats(
  organizationId: string,
  emails: string[],
): Promise<{ sent: number; failed: number }> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { data: sesion } = await supabase.auth.getSession()
  const token = sesion?.session?.access_token
  if (!token) throw new Error('Sesion caducada. Vuelve a entrar.')

  const res = await fetch('/api/admin/invite-seats', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ organizationId, emails }),
  })

  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(body?.error ?? 'No se pudieron enviar las invitaciones')
  }

  return { sent: Number(body.sent ?? 0), failed: Number(body.failed ?? 0) }
}

/**
 * Abre el pago de N asientos y devuelve la URL de Stripe.
 *
 * Lo puede lanzar quien gestiona la formacion de la empresa o un
 * administrador de la plataforma en su nombre.
 */
export async function checkoutSeats(
  organizationId: string,
  seats: number,
): Promise<string> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexion con la base de datos')

  const { data: sesion } = await supabase.auth.getSession()
  const token = sesion?.session?.access_token
  if (!token) throw new Error('Sesion caducada. Vuelve a entrar.')

  const res = await fetch('/api/stripe/checkout-seats', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ organizationId, seats }),
  })

  const body = await res.json().catch(() => ({}))
  if (!res.ok || !body?.url) {
    throw new Error(body?.error ?? 'No se pudo abrir el pago')
  }
  return body.url as string
}

// ── Solicitudes de empresas ───────────────────────────────

export type LeadStatus = 'nuevo' | 'contactado' | 'propuesta' | 'cerrado' | 'descartado'

export interface BusinessLead {
  id: string
  company: string
  contactName: string
  email: string
  phone: string | null
  teamSize: string | null
  message: string | null
  status: LeadStatus
  notes: string | null
  createdAt: string
}

export async function listLeads(): Promise<BusinessLead[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase
    .from('business_lead')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200)

  if (error) {
    console.info('[leads] list:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    company: r.company as string,
    contactName: (r.contact_name as string) ?? '',
    email: r.email as string,
    phone: (r.phone as string) ?? null,
    teamSize: (r.team_size as string) ?? null,
    message: (r.message as string) ?? null,
    status: (r.status as LeadStatus) ?? 'nuevo',
    notes: (r.notes as string) ?? null,
    createdAt: r.created_at as string,
  }))
}

export async function setLeadStatus(
  leadId: string,
  status: LeadStatus,
  notes?: string,
): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  // D-450: antes se tragaba el error con console.error y el llamador
  // (ListaSolicitudes en AdminOrganizationsPage) usaba useMutation
  // sin onError. El admin cambiaba el estado de un lead comercial
  // (por ejemplo de 'nuevo' a 'cerrado') y no se enteraba si el
  // update fallaba: la UI enseñaba el cambio (optimistic) pero al
  // recargar volvia al valor anterior. Ahora se sigue el patron de
  // D-447: se tira, y el toast del componente lo pinta.
  const { error } = await supabase.rpc('update_lead_status', {
    p_lead_id: leadId,
    p_status: status,
    p_notes: notes ?? null,
  })
  if (error) throw new Error(error.message)
}

/**
 * La empresa que le da acceso a quien ha iniciado sesion.
 *
 * Distinto de fetchMyOrganization, que devuelve la que gestiona. Un
 * empleado con asiento no gestiona nada, pero necesita saber de
 * donde viene su acceso: si no, la plataforma le ofrece comprar algo
 * que su empresa ya paga.
 */
export interface MySeat {
  organizationName: string
  courseSlug: string | null
  expiresAt: string | null
}

export async function fetchMySeat(): Promise<MySeat | null> {
  if (!isSupabaseEnabled()) return null
  const supabase = getSupabase()
  if (!supabase) return null

  const { data, error } = await supabase.rpc('my_seat_organization')
  if (error || !data || data.length === 0) return null

  const r = data[0] as Record<string, unknown>
  return {
    organizationName: r.organization_name as string,
    courseSlug: (r.course_slug as string) ?? null,
    expiresAt: (r.expires_at as string) ?? null,
  }
}


// ── Rol dentro de la organizacion ─────────────────────────

/**
 * Que puede hacer quien ha iniciado sesion en su organizacion.
 *
 * Gestionar el programa y participar en el son cosas distintas y
 * pueden darse por separado: alguien de Recursos Humanos puede
 * seguir el avance sin hacer el programa, y un participante no ve
 * el panel.
 */
export interface OrganizationRole {
  organizationId: string
  organizationName: string
  /** Ve el panel de seguimiento. */
  isManager: boolean
  /** Tiene acceso al contenido. */
  hasLicence: boolean
  courseSlug: string | null
  expiresAt: string | null
}

export async function fetchMyRole(): Promise<OrganizationRole | null> {
  if (!isSupabaseEnabled()) return null
  const supabase = getSupabase()
  if (!supabase) return null

  const { data, error } = await supabase.rpc('my_organization_role')
  if (error || !data || data.length === 0) return null

  const r = data[0] as Record<string, unknown>
  return {
    organizationId: r.organization_id as string,
    organizationName: r.organization_name as string,
    isManager: Boolean(r.is_manager),
    hasLicence: Boolean(r.has_licence),
    courseSlug: (r.course_slug as string) ?? null,
    expiresAt: (r.expires_at as string) ?? null,
  }
}

// ── Donde se atasca la plantilla ──────────────────────────

export interface ModuleFunnelRow {
  moduleSlug: string
  started: number
  completed: number
  completionPct: number
}

export interface AtRiskMember {
  email: string
  completionPct: number
  daysInactive: number
}

/**
 * Cuanta gente empieza y termina cada modulo.
 *
 * Es la lectura que mas usan los responsables de formacion: si el
 * 85% completa el primero y el 40% el tercero, el problema esta en
 * el tercero, no en la plantilla.
 */
export async function fetchModuleFunnel(organizationId: string): Promise<ModuleFunnelRow[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('team_module_funnel', {
    p_organization_id: organizationId,
  })
  if (error) {
    console.info('[organizations] funnel:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    moduleSlug: r.module_slug as string,
    started: Number(r.started ?? 0),
    completed: Number(r.completed ?? 0),
    completionPct: Number(r.completion_pct ?? 0),
  }))
}

/** Quien empezo y lleva tiempo sin volver. */
export async function fetchAtRisk(
  organizationId: string,
  days = 14,
): Promise<AtRiskMember[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  const { data, error } = await supabase.rpc('team_at_risk', {
    p_organization_id: organizationId,
    p_days: days,
  })
  if (error) {
    console.info('[organizations] atRisk:', error.message)
    return []
  }

  return (data ?? []).map((r: Record<string, unknown>) => ({
    email: (r.email as string) ?? '',
    completionPct: Number(r.completion_pct ?? 0),
    daysInactive: Number(r.days_inactive ?? 0),
  }))
}
